import React, { useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { ModalShell } from '../../../shared/modals/ModalShell'
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { posSharedUi as posUi } from '../../../shared/ui/posSharedUi';
import { showError, showSuccess } from '../../../shared/ui/appToast';
import { getErrorMessage } from '../../../shared/errorMessage';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { useAuthStore } from '../../../auth/store/useAuthStore';
import { usePlanEntitlements } from '../../../shell/providers/PlanEntitlementsProvider';
import { useBusinessProfileStore } from '../../../shell/session/store/useBusinessProfileStore';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';
import { buildSupportTicketContext } from '../../../shell/support/supportTicketContext';
import { insertSupportTicket } from '../../../shell/support/services/supportTicketsRepo';
import { hasRecentCatalogCategoryRequest } from '../../../onboarding/application/otherRubroSupportTicket';

type Props = {
  visible: boolean;
  onClose: () => void;
};

export function CategoryRequestModal({ visible, onClose }: Props) {
  const userId = useAuthStore((s) => s.session?.user?.id);
  const { entitlements } = usePlanEntitlements();
  const businessProfile = useBusinessProfileStore((s) => s.profile);
  const branchId = useSessionStore((s) => s.currentBranchId ?? null);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setComment('');
    setNameError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    const categoryName = name.trim();
    if (categoryName.length < 2) {
      setNameError('Escribí al menos 2 caracteres.');
      return;
    }
    setNameError(null);
    const orgId = entitlements?.organization_id;
    if (!userId || !orgId) {
      showError('No se pudo enviar', 'Falta sesión u organización.');
      return;
    }
    setSubmitting(true);
    try {
      const duplicate = await hasRecentCatalogCategoryRequest(orgId, categoryName);
      if (duplicate) {
        showError('Ya lo pediste', `Ya pediste "${categoryName}" recientemente. Te avisamos por soporte.`);
        handleClose();
        return;
      }
      const baseContext = await buildSupportTicketContext({
        branchId,
        branchName:
          useSessionStore.getState().branches.find((b) => b.id === branchId)?.name ?? null,
      });
      const bodyLines = [`Pedido de categoría: ${categoryName}`];
      const trimmedComment = comment.trim();
      if (trimmedComment) bodyLines.push(trimmedComment);

      await insertSupportTicket({
        organizationId: orgId,
        branchId,
        userId,
        ticketType: 'improvement',
        body: bodyLines.join('\n\n'),
        context: {
          ...baseContext,
          source: 'catalog_category_request',
          requested_category_name: categoryName,
          business_type_code: businessProfile?.business_type_code ?? null,
          comment: trimmedComment || null,
        },
      });
      showSuccess('Pedido enviado', 'Lo vemos en soporte y te avisamos cuando esté.', 4500);
      handleClose();
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'catalog', op: 'category_request.create', error: e, userMessage: 'No se pudo enviar el pedido.' });
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = name.trim().length >= 2 && !submitting;

  return (
    <ModalShell
      visible={visible}
      onClose={handleClose}
      title="Pedir categoría"
      appearance="ticket"
      kicker="Catálogo"
      closeAccessibilityLabel="Cerrar pedir categoría"
      footer={
        <View style={[posUi.modalActions, modalExperience.footerActions]}>
          <TouchableOpacity
            style={[posUi.secondaryButton, posUi.modalButtonHalf]}
            onPress={handleClose}
            disabled={submitting}
            accessibilityRole="button"
            accessibilityLabel="Cancelar"
          >
            <Text style={posUi.secondaryButtonText}>Cancelar</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[posUi.primaryButton, posUi.modalButtonHalf, !canSubmit && posUi.disabledCard]}
            onPress={() => void handleSubmit()}
            disabled={!canSubmit}
            accessibilityRole="button"
            accessibilityLabel="Enviar pedido"
            testID="catalog-category-request-submit"
          >
            {submitting ? (
              <ActivityIndicator color={ticketColors.goldInk} />
            ) : (
              <Text style={posUi.primaryButtonText}>Enviar pedido</Text>
            )}
          </TouchableOpacity>
        </View>
      }
    >
      <View style={styles.body}>
        <Text style={styles.hint}>
          Contanos qué categoría falta. No la creamos al toque: la revisamos y te avisamos por soporte.
        </Text>
        <Text style={styles.label}>Nombre de la categoría</Text>
        <TextInput
          value={name}
          onChangeText={(value) => {
            setName(value);
            if (nameError) setNameError(null);
          }}
          placeholder="Ej. Accesorios celular"
          placeholderTextColor={ticketColors.ink3}
          style={styles.input}
          editable={!submitting}
          maxLength={80}
          accessibilityLabel="Nombre de la categoría a pedir"
          testID="catalog-category-request-name"
        />
        {nameError ? (
          <Text
            style={styles.fieldError}
            accessibilityRole="alert"
            accessibilityLiveRegion="polite"
            testID="catalog-category-request-name-error"
          >
            {nameError}
          </Text>
        ) : null}
        <Text style={styles.label}>Comentario (opcional)</Text>
        <TextInput
          value={comment}
          onChangeText={setComment}
          placeholder="Ej. Vendo fundas y cargadores"
          placeholderTextColor={ticketColors.ink3}
          style={[styles.input, styles.inputMultiline]}
          editable={!submitting}
          multiline
          maxLength={400}
          accessibilityLabel="Comentario opcional del pedido"
          testID="catalog-category-request-comment"
        />
      </View>
    </ModalShell>
  );
}

const styles = StyleSheet.create({
  body: {
    gap: spacing.sm,
  },
  hint: {
    ...typography.caption,
    color: ticketColors.ink2,
    marginBottom: spacing.xs,
  },
  label: {
    ...typography.caption,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  fieldError: {
    ...typography.caption,
    color: ticketColors.bad,
  },
  input: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: ticketColors.paper,
    color: ticketColors.ink,
    ...typography.body,
  },
  inputMultiline: {
    minHeight: 88,
    textAlignVertical: 'top',
  },
});
